// pages/TransactionsPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TransactionsTable from '../components/TransactionsTable';
import {
  TransactionFilters,
  fetchTransactions,
  createTransaction
} from '../services/transactionsService';
import { BUSINESS_TYPE_ENUM } from '../helpers/HelperConstants';
import { Transaction } from '../helpers/HelperInterfaces';
import { DEFAULT_USER_ID } from '../helpers/HelperConstants';
import { Filter } from 'react-feather';
import { isStringValid } from '../helpers/HelperFunctions';


const defaultFilters = new TransactionFilters();
const draftDefaultFilters = new TransactionFilters(50, true);

const DEFAULT_TRANSACTION_PAGE_SIZE = 50;

interface TransactionsPageProps {
  accountsMap: Record<string, string>;
  onBack: () => void;
}


const TransactionsPage: React.FC<TransactionsPageProps> = ({ accountsMap, onBack }) => {
  // ── Data ──────────────────────────────────────────────────
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [count, setCount]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // -- Adding transactions modal
  const [showAddTransaction, setShowAddTransaction]     = useState(false);
  const [description, setDescription]                   = useState('');
  const [selectedAccount, setSelectedAccount]           = useState('');
  const [newDate, setNewDate]                           = useState('');
  const [newAmount, setNewAmount]                       = useState('');
  const [newBusinessName, setNewBusinessName]           = useState('');
  const [selectedIsExpense, setSelectedIsExpense]       = useState(true);
  const [selectedBusinessType, setSelectedBusinessType] = useState('');
  const [addLoading, setAddLoading]                     = useState(false);
  const [addError, setAddError]                         = useState<string | null>(null);
  const [toUpdateAccount, setToUpdateAccount]           = useState(false);

  // ── Filter panel state ────────────────────────────────────
  const [filterOpen, setFilterOpen]       = useState(false);
  const [draftFilters, setDraftFilters]   = useState<TransactionFilters>(draftDefaultFilters);
  const [activeFilters, setActiveFilters] = useState<TransactionFilters>(defaultFilters);
  const [bizDropOpen, setBizDropOpen]     = useState(false);
  const bizDropRef                        = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage]     = useState(1);


  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchTransactions(DEFAULT_USER_ID, currentPage, activeFilters)
      .then(([returnedTransactions, returnedCount]) => {
        // Safety check to ensure it's a valid array before spreading
        
        setTransactions([...returnedTransactions]);
        setCount(returnedCount);
      })
      .catch((err: Error) => setError("Failed to get transactions. Please try again later."))
      .finally(() => setLoading(false));
  }, [activeFilters, currentPage]); // re-fetches whenever Apply Filters is clicked

  // Close biz dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bizDropRef.current && !bizDropRef.current.contains(e.target as Node)) {
        setBizDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAddTransaction = async () => {
    if (!description.trim()) { setAddError('Description is required.'); return;}
    if (!newBusinessName.trim()) { setAddError('Business name is required.'); return;}

    let [descriptionValid, errorDescriptionMsg] = isStringValid(description.trim())
    let [businessValid, errorBusinessMsg] = isStringValid(newBusinessName.trim())
    if (!descriptionValid) { setAddError(`Invalid description. ${errorDescriptionMsg}`); return;}
    if (!businessValid) { setAddError(`Invalid Business Name. ${errorBusinessMsg}`); return;}

    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount < 0) { setAddError('Please enter a valid amount.'); return;}
    if (!newDate) { setAddError('Please select a date.'); return;}
    if (selectedBusinessType == '') { setAddError('Please select a business type.'); return;}
    if (selectedAccount == '') { setAddError('Please select an account.'); return;}

    setAddLoading(true);
    setAddError(null)
    try {

      let [transaction, transactionErrMsg ] = await createTransaction(DEFAULT_USER_ID, {
        businessName: newBusinessName.trim(),
        businessType: BUSINESS_TYPE_ENUM[selectedBusinessType],
        description: description,
        accountId: selectedAccount,
        shouldUpdateAccount: toUpdateAccount,
        date: newDate,
        amount: amount,
        isExpense: selectedIsExpense
      });

      if (transaction == undefined) {
        throw Error(transactionErrMsg);
      }

      setDescription("");
      setSelectedAccount("");
      setNewBusinessName("");
      setSelectedBusinessType("");
      setToUpdateAccount(false);
      setNewAmount('');
      setCurrentPage(1);
      setActiveFilters((prev) => ({...prev}));
      setShowAddTransaction(false);

    } catch(error: any) {
      setAddError(error.message);
    } finally{
      setAddLoading(false);
    }
  }

  const handleCloseAddTransaction = () => {
    setShowAddTransaction(false);
    setDescription("");
    setSelectedAccount("");
    setSelectedBusinessType("");
    setNewBusinessName('');
    setNewAmount('');
    setSelectedIsExpense(true);
    setNewDate(new Date().toISOString().split('T')[0]);
      setToUpdateAccount(false);
    setAddError(null);
  }

  const handleApplyFilters = () => {

    if(draftFilters.dateFrom > draftFilters.dateTo) {
      setError("Oh no! Start date cannot be later than the end date. Please try again.");
      return
    }

    setActiveFilters({ ...draftFilters });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDraftFilters(draftDefaultFilters);
    setActiveFilters(defaultFilters);
    setCurrentPage(1);
  };

  const toggleBizType = (type: string) => {
    setDraftFilters((prev) => ({
      ...prev,
      businessTypes: prev.businessTypes.includes(type)
        ? prev.businessTypes.filter((t) => t !== type)
        : [...prev.businessTypes, type],
    }));
  };

  const bizTypeLabel =
    draftFilters.businessTypes.length === 0
      ? 'Select types…'
      : draftFilters.businessTypes.length === 1
      ? draftFilters.businessTypes[0]
      : `${draftFilters.businessTypes.length} selected`;



  // Client-side pagination of the fetched filtered result
  const totalPages = Math.max(1, Math.ceil(count / DEFAULT_TRANSACTION_PAGE_SIZE));

  const paginated = useMemo(() => {
    return transactions.slice(0, DEFAULT_TRANSACTION_PAGE_SIZE);
  }, [transactions]);

  const showingFrom = count === 0 ? 0 : (currentPage - 1) * DEFAULT_TRANSACTION_PAGE_SIZE + 1;
  const showingTo   = Math.min((currentPage-1) * DEFAULT_TRANSACTION_PAGE_SIZE + transactions.length, count);

  const handlePrev = useCallback(() => setCurrentPage((p) => Math.max(1, p - 1)), []);
  const handleNext = useCallback(() => setCurrentPage((p) => Math.min(totalPages, p + 1)), [totalPages]);

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      <div className="ac-page">
        {/* Header */}
        <div className="ac-header">
          <h1 className="page-title">Transactions</h1>
          <div className="ac-header-actions">
            <button className="ac-btn ac-btn-primary" onClick={() => setShowAddTransaction(true)}>+ Add Transaction</button>
          </div>
        </div>
      </div>

      {/* Filter toggle bar */}
      <button
        className={`tx-filter-toggle ${filterOpen ? 'tx-filter-toggle--open' : ''}`}
        onClick={() => setFilterOpen((o) => !o)}
        aria-expanded={filterOpen}
      >
        <span className="tx-filter-toggle-label">
          <Filter size={16}/>
          Filter transactions
          {activeFilters !== defaultFilters &&
            Object.values(activeFilters).some((v) =>
              Array.isArray(v) ? v.length > 0 : v !== '' && v !== true
            ) && (
              <span className="tx-filter-active-badge">Active</span>
            )}
        </span>
        <span className={`tx-filter-chevron ${filterOpen ? 'tx-filter-chevron--open' : ''}`}>▾</span>
      </button>

      {/* Filter panel */}
      {filterOpen && (
        <div className="tx-filter-panel">
          <div className="tx-filter-grid">

            {/* Business name */}
            <div className="tx-filter-field">
              <label className="tx-filter-label">Business name</label>
              <input
                className="ac-input"
                type="text"
                placeholder="e.g. Whole Foods"
                value={draftFilters.businessName}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, businessName: e.target.value }))
                }
              />
            </div>

            {/* Type (expense/income) */}
            <div className="tx-filter-field">
              <label className="tx-filter-label">Type</label>
              <div className="tx-filter-checks">
                <label className="tx-filter-check-label">
                  <input
                    type="checkbox"
                    checked={draftFilters.showExpense}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({ ...prev, showExpense: e.target.checked }))
                    }
                  />
                  Expense
                </label>
                <label className="tx-filter-check-label">
                  <input
                    type="checkbox"
                    checked={draftFilters.showIncome}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({ ...prev, showIncome: e.target.checked }))
                    }
                  />
                  Income
                </label>
              </div>
            </div>

            {/* Business type dropdown */}
            <div className="tx-filter-field" ref={bizDropRef} style={{ position: 'relative' }}>
              <label className="tx-filter-label">Business type</label>
              <button
                className="tx-biz-dropdown-btn ac-input"
                onClick={() => setBizDropOpen((o) => !o)}
                type="button"
              >
                <span style={{ color: draftFilters.businessTypes.length === 0 ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                  {bizTypeLabel}
                </span>
                <span className={`tx-filter-chevron ${bizDropOpen ? 'tx-filter-chevron--open' : ''}`}>▾</span>
              </button>

              {bizDropOpen && (
                <div className="tx-biz-dropdown">
                  {Object.keys(BUSINESS_TYPE_ENUM).length === 0 && (
                    <div className="tx-biz-dropdown-empty">No types available</div>
                  )}
                  {Object.keys(BUSINESS_TYPE_ENUM).map((type) => (
                    <label key={type} className="tx-biz-dropdown-item">
                      <input
                        type="checkbox"
                        checked={draftFilters.businessTypes.includes(type)}
                        onChange={() => toggleBizType(type)}
                      />
                      {type}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Date from */}
            <div className="tx-filter-field">
              <label className="tx-filter-label">Date from</label>
              <input
                className="ac-input"
                type="date"
                value={draftFilters.dateFrom}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                }
              />
            </div>

            {/* Spacer — keeps date to aligned to right column */}
            <div className="tx-filter-field tx-filter-spacer" />

            {/* Date to */}
            <div className="tx-filter-field">
              <label className="tx-filter-label">Date to</label>
              <input
                className="ac-input"
                type="date"
                value={draftFilters.dateTo}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                }
              />
            </div>

          </div>

          {/* Filter actions */}
          <div className="tx-filter-actions">
            <button className="ac-btn ac-btn-ghost" onClick={handleClearFilters}>
              Clear filters
            </button>
            <button className="ac-btn ac-btn-primary" onClick={handleApplyFilters}>
              Apply filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <TransactionsTable
        transactions={paginated}
        accountsMap={accountsMap}
        loading={loading}
        error={error}
        showAll
      />

      {/* Pagination */}
      {!loading && !error && count > 0 && (
        <div className="tx-pagination">
          <button
            className="ac-btn ac-btn-ghost tx-pagination-btn"
            onClick={handlePrev}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          <span className="tx-pagination-info">
            Showing {showingFrom}–{showingTo} of {count}
          </span>
          <button
            className="ac-btn ac-btn-ghost tx-pagination-btn"
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
      {/* Add Transaction Modal */}
      {showAddTransaction && (
        <div className="ac-modal-overlay" onClick={handleCloseAddTransaction}>
          <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ac-modal-header">
              <h2 className="ac-modal-title">Add Transaction</h2>
              <button className="ac-modal-close" onClick={handleCloseAddTransaction} aria-label="Close">✕</button>
            </div>
            <div className="ac-form">
              <label className="ac-label">
                Description
                <input
                  className="ac-input"
                  type="text"
                  placeholder="e.g. Payment"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="ac-label">
                Business Name
                <input
                  className="ac-input"
                  type="text"
                  placeholder="e.g. Costco"
                  value={newBusinessName}
                  onChange={(e) => setNewBusinessName(e.target.value)}
                />
              </label>

              <label className="ac-label">
                Business Type
                <select value={selectedBusinessType} onChange={(e)=> setSelectedBusinessType(e.target.value)}  className="ac-input">
                  <option value="">Select business type</option>
                  {Object.keys(BUSINESS_TYPE_ENUM).map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </label>

              <label className="ac-label">
                Amount
                <input
                  className="ac-input"
                  type="number"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                />
              </label>
              <label className="ac-label">
                Account
                <select value={selectedAccount} onChange={(e)=> setSelectedAccount(e.target.value)} className="ac-input">
                  <option value="">Select account</option>
                  {
                    Object.entries(accountsMap).map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))
                  }
                </select>
                <div className="tx-filter-checks" style={{ paddingTop: 4 }}>
                  <label className="tx-filter-check-label">
                    <input type="checkbox" name="tx-type" checked={toUpdateAccount} onChange={() => setToUpdateAccount(!toUpdateAccount)}></input>
                    update account?
                  </label>
                </div>
              </label>

              <label className="ac-label">
                Date
                <input
                  className="ac-input"
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </label>

              <label className="ac-label">
                Type
                <div className="tx-filter-checks" style={{ paddingTop: 4 }}>
                  <label className="tx-filter-check-label">
                    <input
                      type="radio"
                      name="tx-type"
                      checked={selectedIsExpense}
                      onChange={() => setSelectedIsExpense(true)}
                    />
                    Expense
                  </label>
                  <label className="tx-filter-check-label">
                    <input
                      type="radio"
                      name="tx-type"
                      checked={!selectedIsExpense}
                      onChange={() => setSelectedIsExpense(false)}
                    />
                    Income
                  </label>
                </div>
              </label>

              {addError && <p className="ac-error-msg">{addError}</p>}

              <div className="ac-modal-actions">
                <button className="ac-btn ac-btn-ghost" onClick={handleCloseAddTransaction}>
                  Cancel
                </button>
                <button
                  className="ac-btn ac-btn-primary"
                  onClick={handleAddTransaction}
                  disabled={addLoading}
                >
                  {addLoading ? 'Adding…' : 'Add Transaction'}
                </button>
              </div>
              </div>
              </div>
              </div>
      )}
    </>
  );
};

export default TransactionsPage;